/**
 * 🎁 HEALING SOUNDS — FREE GIFT ENGINE
 *
 * In-house auto-add free gift system. Replaces EasyGift app for full compatibility
 * with custom slide-cart + Whop external checkout.
 *
 * Install: theme.liquid loads this script after hs-checkout.js
 * Config : assets/free-gift-rules.json (see docs/FREE_GIFT_ENGINE.md)
 *
 * Architecture:
 *   [cart event fired] → evaluate rules → add/remove gift via /cart/add.js or /cart/change.js
 *   → slide-cart's waitForAppsToProcess polling detects cart change → drawer refreshes
 *
 * Gift items are tagged via line-item properties:
 *   - _free_gift: "true"        → detected by slide-cart for "FREE" badge + hs-checkout for Whop sync
 *   - _free_gift_rule: "<ruleId>" → tracks which rule added this gift (for dedup / remove)
 *
 * Properties prefixed with _ are hidden from cart note, but accessible via /cart.js.
 */

(function initHSFreeGift() {
  if (window.__HS_FREE_GIFT_LOADED__) return;
  window.__HS_FREE_GIFT_LOADED__ = true;

  const LOG_PREFIX = '%c🎁 [HS FreeGift]';
  const LOG_STYLE = 'background: #10B981; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold;';
  const DEBUG = window.HS_FREE_GIFT_DEBUG === true; // set window.HS_FREE_GIFT_DEBUG = true to enable verbose logs

  const log = (...args) => console.log(LOG_PREFIX, LOG_STYLE, ...args);
  const debug = (...args) => { if (DEBUG) console.log(LOG_PREFIX, LOG_STYLE, ...args); };
  const warn = (...args) => console.warn(LOG_PREFIX, LOG_STYLE, ...args);

  // Line item properties keys
  const PROP_FREE = '_free_gift';
  const PROP_RULE_ID = '_free_gift_rule';

  // State
  let RULES = null;
  let isProcessing = false;
  let pendingEval = false;

  // Config discovery — data-rules-url on <script id="hs-free-gift-engine"> OR meta fallback
  const scriptEl = document.getElementById('hs-free-gift-engine') ||
                   document.querySelector('script[src*="free-gift-engine"]');
  const RULES_URL = scriptEl?.dataset?.rulesUrl ||
                    document.querySelector('meta[name="hs-free-gift-rules"]')?.content;

  if (!RULES_URL) {
    warn('rules URL not found. Set data-rules-url on script tag or meta name="hs-free-gift-rules".');
    return;
  }

  log('Initializing, rules URL:', RULES_URL);

  // ─────────────────────────────────────────
  // Load rules
  // ─────────────────────────────────────────

  async function loadRules() {
    try {
      const res = await fetch(RULES_URL, { cache: 'no-store' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      RULES = json;
      const activeCount = (json.rules || []).filter(r => r.enabled).length;
      log(`Rules loaded: ${activeCount} active / ${(json.rules || []).length} total`);
    } catch (e) {
      warn('Failed to load rules:', e.message);
      RULES = { enabled: false, rules: [] };
    }
  }

  // ─────────────────────────────────────────
  // Collection membership cache (for 'collection' trigger)
  // ─────────────────────────────────────────

  // Map<collectionHandle, Set<product_id>> — populated on load for enabled collection rules
  const collectionMembership = new Map();

  async function loadCollectionMembership(handle) {
    if (collectionMembership.has(handle)) return collectionMembership.get(handle);
    const productIds = new Set();
    let page = 1;
    const MAX_PAGES = 20; // safety : 250 × 20 = 5000 products max per collection
    try {
      while (page <= MAX_PAGES) {
        const res = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`, { cache: 'default' });
        if (!res.ok) break;
        const data = await res.json();
        const products = data.products || [];
        if (products.length === 0) break;
        for (const p of products) productIds.add(p.id);
        if (products.length < 250) break;
        page++;
      }
      collectionMembership.set(handle, productIds);
      debug(`Collection '${handle}': ${productIds.size} product IDs cached`);
    } catch (e) {
      warn(`Failed to load collection '${handle}':`, e.message);
      collectionMembership.set(handle, productIds); // mark as loaded (with what we got) to avoid loop
    }
    return productIds;
  }

  async function preloadCollectionsForRules() {
    if (!RULES || !Array.isArray(RULES.rules)) return;
    const toLoad = new Set();
    for (const rule of RULES.rules) {
      if (!rule.enabled) continue;
      if (rule.trigger?.type === 'collection' && Array.isArray(rule.trigger.collections)) {
        rule.trigger.collections.forEach(h => toLoad.add(h));
      }
    }
    if (toLoad.size === 0) return;
    log(`Pre-loading ${toLoad.size} collection memberships: [${[...toLoad].join(', ')}]`);
    await Promise.all([...toLoad].map(h => loadCollectionMembership(h)));
    log(`Collections loaded: ${[...collectionMembership.entries()].map(([h, s]) => `${h}(${s.size})`).join(', ')}`);
  }

  // ─────────────────────────────────────────
  // Product tag cache (for type:'tag' trigger)
  // ─────────────────────────────────────────
  //
  // /cart.js does NOT include product tags. We fetch /products/<handle>.js on demand
  // and cache the tags set per handle. Cache is per-page-load (cleared on navigation).
  // Multiple cart mutations reuse the cache — zero cost after first evaluation.

  const productTagsCache = new Map(); // handle (string) → Set<tag> (lowercase)

  async function getProductTags(handle) {
    if (productTagsCache.has(handle)) return productTagsCache.get(handle);
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { cache: 'default' });
      if (!res.ok) {
        productTagsCache.set(handle, new Set());
        return productTagsCache.get(handle);
      }
      const data = await res.json();
      const tags = new Set((data.tags || []).map(t => String(t).toLowerCase()));
      productTagsCache.set(handle, tags);
      debug(`Tags for "${handle}":`, [...tags].slice(0, 10).join(', ') + (tags.size > 10 ? '...' : ''));
      return tags;
    } catch (e) {
      warn(`getProductTags error for "${handle}":`, e.message);
      productTagsCache.set(handle, new Set());
      return productTagsCache.get(handle);
    }
  }

  // Preload tags for all non-gift cart items (only if any rule uses type:'tag').
  // Parallel fetches — N small JSONs typically <2KB each.
  async function preloadCartItemTags(cart) {
    const hasTagRules = RULES.rules.some(r => r.enabled && r.trigger?.type === 'tag');
    if (!hasTagRules) return;
    const handles = [...new Set(cart.items
      .filter(i => !itemIsGift(i))
      .map(i => i.handle)
      .filter(Boolean))];
    if (handles.length === 0) return;
    await Promise.all(handles.map(h => getProductTags(h)));
  }

  // ─────────────────────────────────────────
  // Rule gifts helpers (multi-gift support)
  // ─────────────────────────────────────────

  // A rule can have either:
  //   - `gift: { variantId, productHandle, quantity }` (single gift, legacy)
  //   - `gifts: [{...}, {...}]` (multiple gifts, new)
  // This helper normalizes both to an array.
  function getRuleGifts(rule) {
    if (Array.isArray(rule.gifts)) return rule.gifts;
    if (rule.gift) return [rule.gift];
    return [];
  }

  // ─────────────────────────────────────────
  // Trigger evaluation
  // ─────────────────────────────────────────

  function getNonGiftItems(cart) {
    return cart.items.filter(i => !itemIsGift(i));
  }

  function itemIsGift(item) {
    // Properties in /cart.js are an object: { _free_gift: "true", ... }
    return item.properties && item.properties[PROP_FREE] === 'true';
  }

  function shouldTrigger(rule, cart) {
    const trigger = rule.trigger;
    const nonGiftItems = getNonGiftItems(cart);

    if (nonGiftItems.length === 0) return false;

    switch (trigger.type) {
      case 'product':
        // trigger.productHandles: [handle, ...] — match any item with handle in list
        if (!Array.isArray(trigger.productHandles)) return false;
        return nonGiftItems.some(i => trigger.productHandles.includes(i.handle));

      case 'productId':
        // trigger.productIds: [id, ...] — match any item with product_id in list
        if (!Array.isArray(trigger.productIds)) return false;
        return nonGiftItems.some(i => trigger.productIds.includes(i.product_id));

      case 'productType': {
        // trigger.productTypes: [type, ...] — match any item with product_type in list.
        // /cart.js DOES include product_type on each line item (unlike tags), so this is a
        // synchronous check (no fetch needed). Case-insensitive + trim.
        if (!Array.isArray(trigger.productTypes)) return false;
        const targets = trigger.productTypes.map(t => String(t).toLowerCase().trim()).filter(Boolean);
        if (targets.length === 0) return false;
        return nonGiftItems.some(item => {
          const pt = String(item.product_type || '').toLowerCase().trim();
          return targets.includes(pt);
        });
      }

      case 'tag': {
        // trigger.tags: [tag, ...] — checks if any non-gift cart item has one of these tags.
        // Tags are pre-fetched via /products/<handle>.js in evaluate() before this function
        // runs, so we just read from the cache here (synchronous).
        if (!Array.isArray(trigger.tags)) return false;
        const targetTags = trigger.tags.map(t => String(t).toLowerCase());
        return nonGiftItems.some(item => {
          const tags = productTagsCache.get(item.handle);
          if (!tags) return false;
          for (const t of targetTags) if (tags.has(t)) return true;
          return false;
        });
      }

      case 'collection':
        // trigger.collections: [handle, ...] — checks if any non-gift item's product_id
        // is a member of one of the listed collections. Collection membership is
        // fetched on page load via /collections/<handle>/products.json (paginated, 250/page).
        if (!Array.isArray(trigger.collections)) return false;
        {
          const allLoaded = trigger.collections.every(h => collectionMembership.has(h));
          if (!allLoaded) {
            debug(`Rule ${rule.id}: collection memberships not yet loaded, skipping eval`);
            return false;
          }
          return nonGiftItems.some(item =>
            trigger.collections.some(h => collectionMembership.get(h)?.has(item.product_id))
          );
        }

      case 'cartValue':
        // trigger.minAmount (cents), trigger.maxAmount (optional, cents)
        const nonGiftTotal = nonGiftItems.reduce((s, i) => s + (i.final_line_price || 0), 0);
        if (typeof trigger.minAmount === 'number' && nonGiftTotal < trigger.minAmount) return false;
        if (typeof trigger.maxAmount === 'number' && nonGiftTotal > trigger.maxAmount) return false;
        return true;

      default:
        warn(`Rule ${rule.id}: unknown trigger type "${trigger.type}"`);
        return false;
    }
  }

  // Returns ALL cart items tagged with this rule's id (a rule can have multiple gifts).
  function findExistingGifts(rule, cart) {
    return cart.items.filter(i => i.properties && i.properties[PROP_RULE_ID] === rule.id);
  }

  // Backwards-compat : returns first matching gift (used by window.HSFreeGift API consumers).
  function findExistingGift(rule, cart) {
    return findExistingGifts(rule, cart)[0];
  }

  // ─────────────────────────────────────────
  // Cart mutations
  // ─────────────────────────────────────────

  // Single-gift add : convenience wrapper around batchAddGifts (used by external API).
  async function addGift(rule) {
    const gifts = getRuleGifts(rule);
    if (gifts.length === 0) {
      warn(`Rule "${rule.id}" has no gifts configured`);
      return false;
    }
    return batchAddGifts(gifts.map(g => ({ rule, gift: g })));
  }

  // 🎁 Batch add multiple gifts (possibly from different rules) in a SINGLE /cart/add.js call.
  // Shopify Ajax API: POST /cart/add.js with body { items: [...] } adds all items atomically.
  // For a rule with 4 gifts, this is 4× faster than sequential adds.
  async function batchAddGifts(toAdd) {
    if (!toAdd || toAdd.length === 0) return false;
    try {
      const items = toAdd.map(({ rule, gift }) => ({
        id: gift.variantId,
        quantity: gift.quantity || 1,
        properties: {
          [PROP_FREE]: 'true',
          [PROP_RULE_ID]: rule.id
        }
      }));
      const description = toAdd.map(x => `${x.rule.id}(${x.gift.variantId})`).join(', ');
      debug(`Batch adding ${items.length} gifts: ${description}`);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) {
        const text = await res.text();
        warn(`Batch add failed:`, res.status, text.slice(0, 200));
        return false;
      }
      log(`✅ Batch added ${items.length} gift(s): ${description}`);
      return true;
    } catch (e) {
      warn(`Batch add error:`, e.message);
      return false;
    }
  }

  async function removeGift(item, rule) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: 0
        })
      });
      if (!res.ok) {
        const text = await res.text();
        warn(`Remove gift failed for rule "${rule.id}":`, res.status, text.slice(0, 200));
        return false;
      }
      log(`🗑 Removed gift for rule "${rule.id}"`);
      return true;
    } catch (e) {
      warn(`Remove gift error for rule "${rule.id}":`, e.message);
      return false;
    }
  }

  // 🎁 2026-04-22 (bug fix) : batch-remove multiple gifts in a SINGLE /cart/update.js call.
  // Previously: sequential /cart/change.js × N gifts → 300-500ms × N = 1-2s for 2 gifts.
  // Now: single atomic call → ~200-300ms regardless of number of gifts.
  // Uses variant_id as key (/cart/update.js `updates` format).
  async function batchRemoveGifts(itemsWithRules) {
    if (!itemsWithRules || itemsWithRules.length === 0) return false;
    try {
      const updates = {};
      for (const { item } of itemsWithRules) {
        updates[item.variant_id] = 0;
      }
      const ruleIds = itemsWithRules.map(x => x.rule.id).join(', ');
      debug(`Batch removing ${itemsWithRules.length} gifts (rules: ${ruleIds}) via /cart/update.js`);
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!res.ok) {
        const text = await res.text();
        warn(`Batch remove failed:`, res.status, text.slice(0, 200));
        return false;
      }
      log(`🗑 Batch removed ${itemsWithRules.length} gift(s): ${ruleIds}`);
      return true;
    } catch (e) {
      warn(`Batch remove error:`, e.message);
      return false;
    }
  }

  // ─────────────────────────────────────────
  // /cart page section refresh (Shopify Section Rendering API)
  // ─────────────────────────────────────────

  // After engine adds/removes a gift while user is on /cart page, re-render the main-cart
  // section in place (no full page reload). Shopify exposes section HTML via ?sections=<id>.
  async function refreshMainCartSection() {
    try {
      // Find the main-cart section wrapper in the current DOM.
      // Shopify renders sections as <div id="shopify-section-<section-id>" class="shopify-section shopify-section__main-cart">.
      // We look for the one containing a .cart-recap (unique to main-cart).
      const sectionEl = document.querySelector('[id^="shopify-section-"]:has(.cart-recap), [id^="shopify-section-"]:has(.line-item)');
      if (!sectionEl) {
        debug('refreshMainCartSection: section element not found, skipping (engine changes will show on next reload)');
        return;
      }
      const sectionId = sectionEl.id.replace(/^shopify-section-/, '');
      debug('refreshMainCartSection: fetching section', sectionId);

      const res = await _origFetch(`/cart?section_id=${encodeURIComponent(sectionId)}&t=${Date.now()}`, {
        headers: { 'Accept': 'text/html', 'Cache-Control': 'no-cache' }
      });
      if (!res.ok) {
        warn('refreshMainCartSection: fetch failed', res.status);
        return;
      }
      const html = await res.text();
      // Replace the section innerHTML
      sectionEl.innerHTML = html;
      log('Main cart section refreshed in place');
    } catch (e) {
      warn('refreshMainCartSection error:', e.message);
    }
  }

  // ─────────────────────────────────────────
  // Core evaluation
  // ─────────────────────────────────────────

  async function evaluate(reason = 'manual') {
    if (!RULES || !RULES.enabled) {
      debug('Engine disabled or no rules loaded');
      return;
    }

    if (isProcessing) {
      debug('Already processing, queuing another eval');
      pendingEval = true;
      return;
    }

    isProcessing = true;
    debug(`Evaluating (reason: ${reason})`);

    try {
      const cartRes = await fetch('/cart.js', { cache: 'no-store' });
      if (!cartRes.ok) throw new Error('cart.js HTTP ' + cartRes.status);
      const cart = await cartRes.json();

      // 🎁 Preload product tags for any type:'tag' rule (parallel fetches, cached)
      await preloadCartItemTags(cart);

      let changed = false;

      // 🎁 Collect first, then batch-remove + batch-add (both atomic single HTTP calls).
      //
      // Multi-gift rules (e.g. bowl-set-3-gift has 4 gifts) work correctly:
      //   - `findExistingGifts(rule, cart)` returns all cart items tagged with rule.id
      //   - We compare to `getRuleGifts(rule)` to find missing + extra
      //   - Missing → toAdd ; extra → toRemove (e.g., rule variantId changed)
      //
      // Batch operations avoid the 2s delay when N gifts need removal/add.
      //   Sequential N × /cart/change.js = 1-2s → single batch call = ~200-300ms
      const toAdd = [];    // [{ rule, gift }]
      const toRemove = []; // [{ item, rule }]

      for (const rule of RULES.rules) {
        if (!rule.enabled) continue;

        const shouldApply = shouldTrigger(rule, cart);
        const existingGifts = findExistingGifts(rule, cart);
        const ruleGifts = getRuleGifts(rule);

        debug(`Rule "${rule.id}": shouldApply=${shouldApply}, existing=${existingGifts.length}/${ruleGifts.length}`);

        if (shouldApply) {
          // Find missing gifts (configured but not in cart)
          const existingVariantIds = new Set(existingGifts.map(i => i.variant_id));
          for (const giftConfig of ruleGifts) {
            if (!existingVariantIds.has(giftConfig.variantId)) {
              toAdd.push({ rule, gift: giftConfig });
            }
          }
          // Find extras (in cart but not in current rule config — e.g. rule variantId was changed)
          const ruleVariantIds = new Set(ruleGifts.map(g => g.variantId));
          for (const item of existingGifts) {
            if (!ruleVariantIds.has(item.variant_id)) {
              toRemove.push({ item, rule });
            }
          }
        } else if (existingGifts.length > 0 && rule.options?.removeWhenConditionsNotMet !== false) {
          // Condition no longer met → remove all gifts for this rule
          for (const item of existingGifts) {
            toRemove.push({ item, rule });
          }
        }
      }

      // Phase 1 : batch-remove all gifts to remove (single /cart/update.js call, all rules)
      if (toRemove.length > 0) {
        const ok = await batchRemoveGifts(toRemove);
        if (ok) changed = true;
      }

      // Phase 2 : batch-add all gifts to add (single /cart/add.js call, all rules)
      if (toAdd.length > 0) {
        const ok = await batchAddGifts(toAdd);
        if (ok) changed = true;
      }

      if (changed) {
        debug('Cart modified by engine, dispatching freeGift:updated event');
        document.dispatchEvent(new CustomEvent('freeGift:updated', {
          bubbles: true,
          detail: { reason }
        }));

        // 🎁 2026-04-22 : sur /cart page, theme.js ne re-render pas automatiquement
        // après un /cart/add.js ou /cart/change.js fait par l'engine. On utilise Shopify
        // Section Rendering API pour rafraîchir la section main-cart en place, sans reload.
        if (location.pathname === '/cart' || location.pathname === '/cart/') {
          await refreshMainCartSection();
        }
      }
    } catch (e) {
      warn('Evaluate error:', e.message);
    } finally {
      isProcessing = false;
      if (pendingEval) {
        pendingEval = false;
        setTimeout(() => evaluate('queued'), 150);
      }
    }
  }

  // ─────────────────────────────────────────
  // Event hooks
  // ─────────────────────────────────────────

  // Listen to cart events. Let slide-cart.js's waitForAppsToProcess handle polling.
  // We just add/remove gifts — slide-cart will detect the change via its own polling.
  const CART_EVENTS = ['product:added', 'product-form:added', 'cart:item-added', 'cart:updated'];
  CART_EVENTS.forEach(eventName => {
    document.addEventListener(eventName, () => {
      // Immediate eval (no setTimeout) so slide-cart's polling sees the gift change
      evaluate(`event:${eventName}`);
    });
  });

  // 🎁 2026-04-22 : fetch interceptor for /cart page mutations.
  // The /cart page (main-cart.liquid) uses native Shopify form submit or theme.js AJAX
  // that doesn't always dispatch 'cart:updated' globally. We detect /cart/add, /cart/change,
  // /cart/update on ANY page and trigger evaluate() so gifts are added/removed correctly.
  function isCartMutationUrl(url) {
    try {
      const u = new URL(url, location.origin);
      if (u.origin !== location.origin) return false;
      const p = u.pathname;
      return p.startsWith('/cart/add') || p.startsWith('/cart/change') || p.startsWith('/cart/update');
    } catch { return false; }
  }

  const _origFetch = window.fetch.bind(window);
  window.fetch = async function(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const res = await _origFetch.apply(this, arguments);
    if (isCartMutationUrl(url)) {
      // Defer eval so Shopify's cart state commits first
      setTimeout(() => evaluate('fetch-intercept:' + new URL(url, location.origin).pathname), 200);
    }
    return res;
  };

  // Initial eval on page load (handles scenarios where user reloads with items in cart)
  document.addEventListener('DOMContentLoaded', async () => {
    await loadRules();
    // Pre-fetch collection memberships for any rule of type 'collection'.
    // This runs in parallel with other theme init; evaluate() below waits for it anyway.
    await preloadCollectionsForRules();
    // Small delay to let theme.js + hs-checkout.js finish their init
    setTimeout(() => evaluate('page-load'), 500);
  });

  // Wait until engine is idle (no in-flight eval, no pending eval queued).
  // Used by hs-checkout.js before redirecting to Whop checkout, to avoid a race
  // where user clicks checkout while engine is still removing a gift after
  // trigger conditions just became unmet.
  async function waitForIdle(maxWaitMs = 3000, pollIntervalMs = 50) {
    const start = Date.now();
    while (isProcessing || pendingEval) {
      if (Date.now() - start > maxWaitMs) {
        warn(`waitForIdle timeout after ${maxWaitMs}ms (isProcessing=${isProcessing}, pendingEval=${pendingEval})`);
        return false;
      }
      await new Promise(r => setTimeout(r, pollIntervalMs));
    }
    return true;
  }

  // Force a synchronous final eval + wait for completion. Useful before checkout.
  async function flush() {
    await waitForIdle();
    await evaluate('flush');
    await waitForIdle();
  }

  // Expose for manual debugging + external coordination (hs-checkout.js uses flush/waitForIdle before Whop redirect)
  window.HSFreeGift = {
    evaluate: () => evaluate('manual'),
    getRules: () => RULES,
    reload: async () => { await loadRules(); return RULES; },
    addGift,
    removeGift,
    waitForIdle,
    flush,
    isProcessing: () => isProcessing,
    _setDebug: (v) => { window.HS_FREE_GIFT_DEBUG = v; }
  };

  log('Ready. Access window.HSFreeGift for debug.');
})();
